import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import {
  buildChallengePublicUrl,
  fetchChallengeBundle,
  formatChallengeError,
  updateChallengeProjectMeta,
} from '../../services/assignmentChallengeService';
import { challengeThemeStyle, type ChallengeProject } from '../../types/assignmentChallenge';

type Props = {
  open: boolean;
  projectId: string | null;
  title: string;
  themeColor?: string;
  onClose: () => void;
};

function safeFileName(name: string): string {
  return (name || '챌린지').replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 40);
}

async function buildQrPosterBlob(url: string, title: string): Promise<Blob | null> {
  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, url, { errorCorrectionLevel: 'M', margin: 1, width: 880 });
  const pad = 60;
  const titleH = 120;
  const canvas = document.createElement('canvas');
  canvas.width = qrCanvas.width + pad * 2;
  canvas.height = qrCanvas.height + pad * 2 + titleH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '700 52px "Pretendard", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif';
  const text = title.length > 22 ? `${title.slice(0, 21)}…` : title;
  ctx.fillText(text || '챌린지', canvas.width / 2, pad + titleH / 2 - 10);
  ctx.drawImage(qrCanvas, pad, pad + titleH);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

const ChallengeShareModal: React.FC<Props> = ({ open, projectId, title, themeColor, onClose }) => {
  const [project, setProject] = useState<ChallengeProject | null>(null);
  const [qrSrc, setQrSrc] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setError('');
    setMessage('');
    void fetchChallengeBundle(projectId, {
      onCached: (b) => {
        if (!cancelled) setProject(b.project);
      },
    })
      .then((b) => {
        if (!cancelled && b) setProject(b.project);
      })
      .catch((e) => {
        if (!cancelled) setError(formatChallengeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const url = project?.shareToken ? buildChallengePublicUrl(project.shareToken) : '';

  useEffect(() => {
    if (!url) {
      setQrSrc('');
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 1, width: 520 }).then((src) => {
      if (!cancelled) setQrSrc(src);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open || !projectId) return null;

  const flash = (msg: string) => {
    setMessage(msg);
    window.setTimeout(() => setMessage((cur) => (cur === msg ? '' : cur)), 2400);
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      flash('링크를 복사했어요.');
    } catch {
      window.prompt('아래 링크를 복사해 주세요.', url);
    }
  };

  const download = async () => {
    if (!url) return;
    setBusy(true);
    try {
      const blob = await buildQrPosterBlob(url, title);
      if (!blob) throw new Error('QR 이미지를 만들지 못했습니다.');
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `챌린지-QR-${safeFileName(title)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(href), 2000);
      flash('QR 이미지를 내려받았어요.');
    } catch (e) {
      setError(formatChallengeError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleShare = async () => {
    if (!project) return;
    setBusy(true);
    setError('');
    try {
      await updateChallengeProjectMeta(project.id, { shareEnabled: !project.shareEnabled });
      setProject({ ...project, shareEnabled: !project.shareEnabled });
      flash(project.shareEnabled ? '링크를 닫았어요. 학생이 입장할 수 없어요.' : '링크를 다시 열었어요.');
    } catch (e) {
      setError(formatChallengeError(e));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="cp-modal-backdrop cp-chl-share-backdrop"
      role="presentation"
      onClick={onClose}
      style={challengeThemeStyle(themeColor)}
    >
      <div
        className="cp-modal cp-chl-share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-chl-share-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-chl-share-head">
          <h3 id="cp-chl-share-title">참여 링크 · QR</h3>
          <button type="button" className="cp-chl-share-close" onClick={onClose} aria-label="닫기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
          </button>
        </div>
        {error ? (
          <p className="cp-chl-share-error" role="alert">
            {error}
          </p>
        ) : null}
        {!project ? (
          <p className="cp-chl-share-loading">불러오는 중…</p>
        ) : (
          <>
            <div className={`cp-chl-share-qr${project.shareEnabled ? '' : ' is-closed'}`}>
              {qrSrc ? <img src={qrSrc} alt={`${title} 참여 QR 코드`} /> : null}
              {!project.shareEnabled ? <span className="cp-chl-share-closed-badge">링크 닫힘</span> : null}
            </div>
            <div className="cp-chl-share-url-row">
              <input className="cp-chl-share-url" value={url} readOnly aria-label="참여 링크" onFocus={(e) => e.target.select()} />
              <button type="button" className="cp-btn-secondary" onClick={() => void copy()}>
                복사
              </button>
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                disabled={!url}
              >
                열기
              </button>
            </div>
            <div className="cp-chl-share-actions">
              <button type="button" className="cp-btn-primary" onClick={() => void download()} disabled={busy || !url}>
                QR 이미지 다운로드
              </button>
              <button type="button" className="cp-btn-secondary" onClick={() => void toggleShare()} disabled={busy}>
                {project.shareEnabled ? '링크 닫기' : '링크 다시 열기'}
              </button>
            </div>
          </>
        )}
        {message ? (
          <p className="cp-chl-share-msg" role="status">
            {message}
          </p>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default ChallengeShareModal;
